/**
 * BkDashboard — schlanke Dashboard-Übersicht für eine BK (erster Reiter).
 *
 * Analog zu `pflege/PbDashboard`, aber ohne Konfigurierbarkeit (keine
 * Settings/Drag/Resize) — feste Widgets im gleichen Card-Look:
 *   1. Kopf-Widget mit Kernstats (Betreuungen gesamt/laufend, Bewertung,
 *      Startdatum, Vertrag)
 *   2. Jahresübersicht via gemeinsamer JahresuebersichtCard (wie beim PB):
 *      Timeline + „Abwesenheit hinzufügen" (Verfügbarkeit / Urlaub,
 *      Enddatum Pflicht) + „Betreuung erstellen"
 *   3. Kompakte Betreuungen-Liste (PB-Name, Zeitraum, Status-Badge)
 *   4. Aufgaben & Notizen als zwei kompakte Widgets (AufgabenNotizenTab
 *      mit only="tasks" / only="notices")
 *
 * Die Daten der Jahresübersicht (Betreuungen, Verfügbarkeit, Aufgaben) lädt
 * die JahresuebersichtCard; die Betreuungen spiegelt sie hierher.
 */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getThemeColors } from '../../../lib/theme';
import { type V1User, type V1Care } from '../../../lib/v1';
import { JahresuebersichtCard } from '../../pflege/JahresuebersichtCard';
import { AufgabenNotizenTab } from '../../pflege/tabs/AufgabenNotizenTab';
import { WidgetCard } from '../../pflege/PbDashboard/WidgetCard';
import { MatchingList } from '../../../components/matching/MatchingList';
import { navigateGlobal } from '../../../lib/route';
import { CareStatusDot } from '../../pflege/PbDashboard/BetreuungenWidget';
import { fmtDate } from '../../pflege/PbDashboard/helpers';
import { Rating } from '../../pflege/profile/controls';
import type { BkTabId } from '../BkDetailPage';

type CareRow = V1Care & {
  pb_name?: string | null;
  bk_name?: string | null;
};

interface Props {
  user: V1User | null;
  dark: boolean;
  /** Wechsel auf einen anderen Tab der BkDetailPage ("Alle anzeigen →"). */
  onSelectTab: (tab: BkTabId) => void;
}

/* ─── Hauptkomponente ────────────────────────────────────────────────────── */

export function BkDashboard({ user, dark, onSelectTab }: Props) {
  const { t } = useTranslation();
  const tc = getThemeColors(dark);

  // Betreuungen lädt die JahresuebersichtCard und spiegelt sie hierher
  // (Kernstats + kompaktes Betreuungen-Widget).
  const [cares, setCares] = useState<CareRow[]>([]);
  const [caresLoading, setCaresLoading] = useState(true);

  if (!user) {
    return (
      <div style={{ padding: 32, textAlign: 'center', color: tc.muted, fontSize: 13 }}>
        {t('betreuung.list.loading')}
      </div>
    );
  }

  const runningCount = cares.filter(c => c.status === 3).length;
  const showCares = (e: React.MouseEvent) => { e.stopPropagation(); onSelectTab('betreuungen'); };
  const showTasks = (e: React.MouseEvent) => { e.stopPropagation(); onSelectTab('aufgaben-notizen'); };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20, paddingBottom: 24 }}>

      {/* ── Kopf-Widget: Kernstats ──────────────────────────────────── */}
      <div style={{ background: tc.card, border: `1px solid ${tc.border}`, borderRadius: 14, padding: '18px 24px' }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
          gap: 18,
        }}>
          <StatItem dark={dark} label={t('betreuung.dashboard.stats.betreuungen_gesamt')}
            value={caresLoading ? '…' : String(cares.length)} />
          <StatItem dark={dark} label={t('betreuung.dashboard.stats.laufend')}
            value={caresLoading ? '…' : String(runningCount)} />
          <StatItem dark={dark} label={t('betreuung.dashboard.stats.bewertung')}
            value={<Rating dark={dark} value={Number(user.rating ?? 0)} />} />
          <StatItem dark={dark} label={t('betreuung.dashboard.stats.startdatum')}
            value={fmtDate(user.startdatum)} />
          <StatItem dark={dark} label={t('betreuung.dashboard.stats.vertrag')}
            value={user.contract != null && String(user.contract) !== '' ? String(user.contract) : '—'} />
        </div>
      </div>

      {/* ── Jahresübersicht — gemeinsame Karte wie beim PB: Timeline,
             „+ Abwesenheit hinzufügen" und „+ Betreuung erstellen" ──── */}
      <JahresuebersichtCard
        user={user}
        collapsible
        collapseKey="bk-dashboard"
        dark={dark}
        onCaresLoaded={(rows, loading) => { setCares(rows as CareRow[]); setCaresLoading(loading); }}
      />

      {/* ── Widget-Grid ─────────────────────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 14 }}>

        {/* Betreuungen (kompakt) */}
        <BkBetreuungenWidget
          dark={dark}
          cares={cares}
          loading={caresLoading}
          onShowAll={showCares}
        />

        {/* Aufgaben */}
        <WidgetCard dark={dark} title={t('betreuung.dashboard.aufgaben')} onShowAll={showTasks}
          showAllLabel={t('betreuung.dashboard.alle_anzeigen')}>
          <div style={{ padding: '10px 14px' }}>
            <AufgabenNotizenTab user={user} dark={dark} only="tasks" />
          </div>
        </WidgetCard>

        {/* Notizen */}
        <WidgetCard dark={dark} title={t('betreuung.dashboard.notizen')} onShowAll={showTasks}
          showAllLabel={t('betreuung.dashboard.alle_anzeigen')}>
          <div style={{ padding: '10px 14px' }}>
            <AufgabenNotizenTab user={user} dark={dark} only="notices" />
          </div>
        </WidgetCard>

        {/* Matching — passende Pflegebedürftige */}
        <WidgetCard
          dark={dark}
          title={t('betreuung.dashboard.matching_title')}
          onShowAll={e => { e.stopPropagation(); navigateGlobal('matching'); }}
          showAllLabel={t('betreuung.dashboard.alle_anzeigen')}
        >
          <div style={{ padding: '10px 14px' }}>
            <MatchingList userId={user.id} dark={dark} mode="compact" limit={5} />
          </div>
        </WidgetCard>

      </div>
    </div>
  );
}

/* ─── Kernstat-Zelle ─────────────────────────────────────────────────────── */

function StatItem({ dark, label, value }: { dark: boolean; label: string; value: React.ReactNode }) {
  const tc = getThemeColors(dark);
  return (
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: tc.muted, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>
        {label}
      </div>
      <div style={{ fontSize: 16, fontWeight: 700, color: tc.heading, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {value}
      </div>
    </div>
  );
}

/* ─── Betreuungen-Liste (kompakt, BK-Sicht → PB-Namen) ───────────────────── */

function BkBetreuungenWidget({ dark, cares, loading, onShowAll }: {
  dark: boolean;
  cares: CareRow[];
  loading: boolean;
  onShowAll: (e: React.MouseEvent) => void;
}) {
  const { t } = useTranslation();
  const tc = getThemeColors(dark);
  // Nächste/aktuelle zuerst: laufend (3) → bestätigt (2) → neu (0), dann nach Startdatum absteigend.
  const prio: Record<number, number> = { 3: 0, 2: 1, 0: 2, 4: 3, 1: 4 };
  const preview = [...cares]
    .sort((a, b) =>
      (prio[a.status] ?? 5) - (prio[b.status] ?? 5) ||
      String(b.startdatum ?? '').localeCompare(String(a.startdatum ?? '')))
    .slice(0, 5);
  return (
    <WidgetCard dark={dark} title={t('betreuung.dashboard.betreuungen')} badge={cares.length || null}
      onShowAll={onShowAll} showAllLabel={t('betreuung.dashboard.alle_anzeigen')} loading={loading}>
      <div>
        {!loading && preview.length === 0 && (
          <div style={{ padding: '10px 14px', fontSize: 12, color: tc.muted, fontStyle: 'italic' }}>
            {t('pflege.cares.empty')}
          </div>
        )}
        {preview.map(c => (
          <div key={c.id} style={{ padding: '8px 14px', borderBottom: `1px solid ${tc.borderSoft}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 12.5, fontWeight: 500, color: tc.heading, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {c.pb_name ?? `PB #${c.pb_user_id}`}
              </div>
              <div style={{ fontSize: 11, color: tc.muted }}>
                {fmtDate(c.startdatum)} – {c.enddatum ? fmtDate(c.enddatum) : '…'}
              </div>
            </div>
            <CareStatusDot status={c.status} dark={dark} />
          </div>
        ))}
      </div>
    </WidgetCard>
  );
}
